import { gql } from '@apollo/client';
import {
  type CustomFieldConditionalRulesComparisonTypes,
  CustomFieldFormats,
  CustomFieldObjectTypes,
  type CustomFieldSource,
  CustomFieldTypes,
  CustomFieldWorkflowSteps,
} from '@generated/graphql';
import type { LanguageCodes } from '@onevesthq/ov-enums';
import { TRANSLATED_STRING_FIELDS } from '../assets/i18n/config';
import type { Organization } from './organization';
import type { TranslatedString } from './shared';

export const DISALLOWED_CHARS_REGEX = /[<>\\|^*`~]/;

export type SelectOption = {
  displayText?: TranslatedString;
  value: string;
};

export type CustomFieldConditionalRule = {
  field: string;
  comparison: CustomFieldConditionalRulesComparisonTypes;
  value: any;
};

export type CustomField<T = CustomFieldTypes> = {
  id?: string;
  format: CustomFieldFormats;
  organization: Organization;
  type: T;
  key: string;
  translatedDescription?: TranslatedString;
  translatedName?: TranslatedString;
  workflowStep?: CustomFieldWorkflowSteps;
  objectType: CustomFieldObjectTypes;
  selectOptions?: SelectOption[];
  additionalInfo?: any;
  externalField?: CustomFieldSource;
  conditionalRules?: CustomFieldConditionalRule[];
  isTriggerRulesEnabled?: boolean;
  isEnabled?: boolean;
  enablePositiveNegativeSign?: boolean;
  enableAggregation?: boolean;
};

export type CustomOption = {
  customField: CustomField;
  enabled: boolean;
  label: TranslatedString;
  required: string;
  additionalInfo?: any;
  position?: number;
};
export type CustomFieldValue<T = CustomFieldTypes> = {
  key: string;
  customField: CustomField<T>;
  selectedOptions?: SelectOption[];
  value?: T extends CustomFieldTypes.Boolean
    ? boolean
    : T extends CustomFieldTypes.Integer
      ? number
      : T extends CustomFieldTypes.Decimal
        ? number
        : T extends CustomFieldTypes.Date
          ? string
          : T extends CustomFieldTypes.SingleSelect
            ? string
            : T extends CustomFieldTypes.MultipleSelect
              ? string[]
              : T extends CustomFieldTypes.CustomObject
                ? any
                : string;
};

export const CUSTOM_FIELDS_FORMAT_MAP: {
  [key in CustomFieldTypes]: CustomFieldFormats[];
} = {
  [CustomFieldTypes.Text]: [
    CustomFieldFormats.SimpleText,
    CustomFieldFormats.TextArea,
    CustomFieldFormats.Email,
    CustomFieldFormats.Phone,
  ],
  [CustomFieldTypes.Integer]: [
    CustomFieldFormats.Number,
    CustomFieldFormats.Currency,
  ],
  [CustomFieldTypes.Decimal]: [
    CustomFieldFormats.Number,
    CustomFieldFormats.Currency,
  ],
  [CustomFieldTypes.SingleSelect]: [
    CustomFieldFormats.RadioButton,
    CustomFieldFormats.DropdownList,
    CustomFieldFormats.SearchList,
  ],
  [CustomFieldTypes.MultipleSelect]: [
    CustomFieldFormats.Checkbox,
    CustomFieldFormats.DropdownList,
    CustomFieldFormats.SearchList,
  ],
  [CustomFieldTypes.Date]: [
    CustomFieldFormats.Date,
    CustomFieldFormats.DateTime,
  ],
  [CustomFieldTypes.CustomObject]: [
    CustomFieldFormats.PhysicalAddress,
    CustomFieldFormats.CustomAgreements,
    CustomFieldFormats.BankAccount,
    CustomFieldFormats.Custom,
  ],
  [CustomFieldTypes.Boolean]: [
    CustomFieldFormats.ToggleSwitch,
    CustomFieldFormats.Checkbox,
  ],
  [CustomFieldTypes.OrganizationUser]: [
    CustomFieldFormats.SimpleText,
    CustomFieldFormats.SearchList,
  ],
};

/**
 * Same string as the Review Allocations sub-step type. The gateway enum does
 * not list it yet, so the app names it here until codegen picks it up.
 */
export const REVIEW_ALLOCATIONS_WORKFLOW_STEP =
  'REVIEW_ALLOCATIONS' as CustomFieldWorkflowSteps;

export const CUSTOM_FIELDS_WORKFLOW_STEP_MAP: {
  [key in CustomFieldObjectTypes]: CustomFieldWorkflowSteps[];
} = {
  [CustomFieldObjectTypes.Household]: [
    CustomFieldWorkflowSteps.Household,
    CustomFieldWorkflowSteps.Approval,
    REVIEW_ALLOCATIONS_WORKFLOW_STEP,
  ],
  [CustomFieldObjectTypes.Entity]: [
    CustomFieldWorkflowSteps.PersonalInformation,
    CustomFieldWorkflowSteps.PersonsOfInterest,
    CustomFieldWorkflowSteps.NonIndividualInformation,
    CustomFieldWorkflowSteps.EmploymentInformation,
    CustomFieldWorkflowSteps.Approval,
    CustomFieldWorkflowSteps.Documents,
    CustomFieldWorkflowSteps.RelationshipInformation,
    CustomFieldWorkflowSteps.ResidencyInformation,
    CustomFieldWorkflowSteps.IncomeAssetsAndDebts,
  ],
  [CustomFieldObjectTypes.Account]: [
    CustomFieldWorkflowSteps.EditAccount,
    CustomFieldWorkflowSteps.Approval,
  ],
  [CustomFieldObjectTypes.SubAccount]: [CustomFieldWorkflowSteps.Approval],
  [CustomFieldObjectTypes.Goal]: [
    CustomFieldWorkflowSteps.EditGoal,
    CustomFieldWorkflowSteps.Approval,
  ],
  [CustomFieldObjectTypes.Transaction]: [],
  [CustomFieldObjectTypes.Holding]: [],
  [CustomFieldObjectTypes.AccountGroup]: [],
  [CustomFieldObjectTypes.ScheduledTransfer]: [],
  [CustomFieldObjectTypes.Transfer]: [],
};
export const CUSTOM_FIELDS_AVIALABLE_SUB_STEP = [
  CustomFieldWorkflowSteps.PersonalInformation,
  CustomFieldWorkflowSteps.PersonsOfInterest,
  CustomFieldWorkflowSteps.NonIndividualInformation,
  CustomFieldWorkflowSteps.EmploymentInformation,
  CustomFieldWorkflowSteps.Approval,
  CustomFieldWorkflowSteps.Documents,
  CustomFieldWorkflowSteps.EditAccount,
  CustomFieldWorkflowSteps.Approval,
  CustomFieldWorkflowSteps.EditGoal,
  CustomFieldWorkflowSteps.Household,
  REVIEW_ALLOCATIONS_WORKFLOW_STEP,
  CustomFieldWorkflowSteps.RelationshipInformation,
  CustomFieldWorkflowSteps.ResidencyInformation,
  CustomFieldWorkflowSteps.IncomeAssetsAndDebts,
];
export const FETCH_CUSTOM_FIELDS = gql`
query fetchCustomFields($input: FetchCustomFieldsInput!) {
  fetchCustomFields(input: $input) {
  customFields{
    id
    key
    type
    format
    objectType
    workflowStep
    translatedName{ ${TRANSLATED_STRING_FIELDS} }
    selectOptions{
      value
      displayText { ${TRANSLATED_STRING_FIELDS} }
    }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    externalField
    conditionalRules {
      field
      comparison
      value
    }
  }
}
}
`;

/**
 * Gets a display name for a custom field with fallback logic.
 * Returns translatedName in user's language, falls back to org's default language, then key, then id, or a fallback value.
 */
export const getCustomFieldDisplayName = (
  translateBackendString: (translatedObject: unknown) => string,
  customField?: {
    translatedName?:
      | TranslatedString
      | Partial<Record<LanguageCodes, string | null | undefined>>
      | null;
    key?: string | null;
    id?: string | null;
  } | null,
  fallback = '',
): string => {
  if (!customField) return fallback;
  return (
    translateBackendString(customField.translatedName) ||
    customField.key ||
    customField.id ||
    fallback
  );
};

/**
 * Collapses duplicate keys (last occurrence wins). Use after concatenating OV
 * and custodian arrays so live custodian values override stale Mongo copies.
 */
export function dedupeCustomFieldValuesByKeyLast(
  fields: CustomFieldValue[] | undefined | null,
): CustomFieldValue[] {
  if (!fields?.length) return [];
  const byKey = new Map<string, CustomFieldValue>();
  for (const f of fields) {
    byKey.set(f.key, f);
  }
  return Array.from(byKey.values());
}

export function mergeOvAndCustodianCustomFields(
  ovCustomFields: CustomFieldValue[],
  custodianCustomFields: CustomFieldValue[],
): CustomFieldValue[] {
  return dedupeCustomFieldValuesByKeyLast([
    ...ovCustomFields,
    ...custodianCustomFields,
  ]);
}
